import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { useNotifyError } from "utils/useNotifyError";
import ModalTitle, { getTriggerIcon, modalCancelButtonSx, modalConfirmButtonSx, modalContentSx, withModalSx } from "./ModalTitle";

// Reusable action buttons sub-component
export const DialogActionsButtons = ({
  loading,
  handleClose,
  closeTitle,
  handleSave,
  saveTitle,
  saveIcon = <SaveIcon />,
  saveEnabled = true,
  isConfirmButton = true,
  isCloseButton = true,
  disableBackdropClose,
}) => {
  return (
    <DialogActions
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 3,
        py: 2,
        gap: 2,
      }}
    >
      {isCloseButton ? (
        <Button
          onClick={() => handleClose(disableBackdropClose)}
          variant="outlined" // Outlined is a good choice for a "cancel" action
          color="error"
          size="medium"
          sx={modalCancelButtonSx}
          startIcon={<CloseIcon />}
          disabled={loading} // Also disable close button when loading
        >
          {closeTitle}
        </Button>
      ) : null}
      {isConfirmButton ? (
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          size="medium"
          startIcon={saveIcon}
          disabled={!saveEnabled || loading}
          sx={modalConfirmButtonSx}
        >
          {saveTitle}
        </Button>
      ) : null}
    </DialogActions>
  );
};

/**
 * A self-contained confirmation modal.
 * It manages its own open/close state and is triggered by a button.
 */
const ConfirmModal = ({
  onConfirm,
  onOpen,
  titleContent,
  // Header extras: icon badge (defaults to the trigger's icon) and a secondary line under the title.
  titleIcon,
  subtitle,
  children,
  message,
  startIcon,
  btnStyle,
  // Props for the trigger button
  titleButton,
  triggerButton,
  // Props to customize the dialog buttons
  saveTitle = "Потвърди",
  saveIcon,
  closeTitle = "Отказ",
  isCloseButton = true,
  isConfirmButton = true,
  size = "medium",
  dialogSize = "sm",
  confirmEnabled = true,
  // Props to pass directly to MUI Dialog component
  dialogProps,
  resetProps = null,
  variant = "contained",
  disableBackdropClose = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const notifyError = useNotifyError();

  const handleOpen = async () => {
    if (!onOpen) {
      setIsOpen(true);
      return;
    }

    const shouldOpen = await onOpen();

    if (shouldOpen !== false) {
      setIsOpen(true);
    }
  };

  // Prevent closing while loading; only reset state when the dialog actually closes.
  const handleClose = disableBackdropClose => {
    if (loading || disableBackdropClose) return;
    setIsOpen(false);
    if (resetProps) resetProps();
  };

  const handleConfirm = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const result = await onConfirm();
      // The onConfirm function is responsible for its own success notification and refresh.
      // We just close the modal on success.

      if (result !== false) {
        setIsOpen(false);
      }
    } catch (error) {
      notifyError(error);
    } finally {
      setLoading(false);
    }
  };

  // Determine the trigger element. Use `triggerButton` if provided, otherwise create a default one.
  const trigger = triggerButton ? (
    React.cloneElement(triggerButton, { onClick: handleOpen })
  ) : (
    <Button
      sx={btnStyle}
      variant={variant}
      size={size}
      onClick={handleOpen}
      startIcon={startIcon || <ThumbUpIcon />}
    >
      {titleButton}
    </Button>
  );

  return (
    <>
      {trigger}
      <Dialog
        open={isOpen}
        onClose={() => handleClose(disableBackdropClose)}
        fullWidth
        maxWidth={dialogSize}
        {...dialogProps} // Spread any additional dialog props
        sx={withModalSx(dialogProps?.sx)}
      >
        <ModalTitle
          icon={titleIcon ?? (triggerButton ? getTriggerIcon(triggerButton) : startIcon)}
          title={titleContent}
          subtitle={subtitle}
        />

        {(message || children) && (
          <DialogContent
            dividers
            sx={modalContentSx}
          >
            {message ? <Typography sx={{ p: 4, fontWeight: "bold" }}>{message}</Typography> : children}
          </DialogContent>
        )}

        <DialogActionsButtons
          loading={loading}
          handleClose={() => handleClose(false)}
          isCloseButton={isCloseButton}
          isConfirmButton={isConfirmButton}
          closeTitle={closeTitle}
          handleSave={handleConfirm}
          saveTitle={saveTitle}
          saveIcon={saveIcon}
          saveEnabled={confirmEnabled}
          disableBackdropClose={disableBackdropClose}
        />
      </Dialog>
    </>
  );
};

export default ConfirmModal;
